import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { counterService, Counter } from "../../services/counter.service";
import { storeService, Store } from "../../services/store.service";
import { showToast } from "../../lib/toast";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { useAuthStore } from "../../stores/authStore";

export function CountersPage() {
  const navigate = useNavigate();
  const { selectedStoreId } = useAuthStore();
  const [counters, setCounters] = useState<Counter[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [storeFilter, setStoreFilter] = useState<string>(selectedStoreId || "");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    id: string;
    name: string;
  }>({ isOpen: false, id: "", name: "" });
  const [isDeleting, setIsDeleting] = useState(false);
  const limit = 20;

  useEffect(() => {
    loadStores();
  }, []);

  useEffect(() => {
    loadCounters();
  }, [page, storeFilter]);

  const loadStores = async () => {
    try {
      const data = await storeService.getAll();
      setStores(data || []);
    } catch (err) {
      setStores([]);
    }
  };

  const loadCounters = async () => {
    try {
      setLoading(true);
      const response = await counterService.getCounters({
        page,
        limit,
        storeId: storeFilter || undefined,
      });
      setCounters(response.data || []);
      setTotal(response.meta?.total || 0);
    } catch (err: any) {
      showToast.error(err.response?.data?.message || "Failed to load counters");
      setCounters([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (id: string, name: string) => {
    setDeleteModal({ isOpen: true, id, name });
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await counterService.deleteCounter(deleteModal.id);
      showToast.success("Counter deleted successfully");
      loadCounters();
    } catch (err: any) {
      showToast.error(
        err.response?.data?.message || "Failed to delete counter",
      );
    } finally {
      setIsDeleting(false);
      setDeleteModal({ isOpen: false, id: "", name: "" });
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Counters</h1>
          <p className="text-sm text-gray-500 mt-1">
            {total} total counter{total !== 1 ? "s" : ""}
          </p>
        </div>
        <button
          onClick={() => navigate("/counters/create")}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          + Add Counter
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <select
            value={storeFilter}
            onChange={(e) => {
              setStoreFilter(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Stores</option>
            {stores.map((store) => (
              <option key={store.id} value={store.id}>
                {store.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading && (
        <div className="text-center py-12">
          <div className="text-gray-500">Loading counters...</div>
        </div>
      )}

      {!loading && counters.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <div className="text-gray-500 mb-4">No counters found</div>
          <button
            onClick={() => navigate("/counters/create")}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Add First Counter
          </button>
        </div>
      )}

      {!loading && counters.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Counter
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Store
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Code
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {counters.map((counter) => (
                  <tr key={counter.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {counter.name}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {counter.store?.name || "-"}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500">
                        {counter.code || "-"}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          counter.status === "active"
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {counter.status.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() =>
                            navigate(`/counters/${counter.id}/edit`)
                          }
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() =>
                            handleDeleteClick(counter.id, counter.name)
                          }
                          className="text-red-600 hover:text-red-800 text-sm font-medium"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="px-6 py-4 border-t flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Page {page} of {totalPages}
              </div>
              <div className="flex gap-2">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                  className="px-3 py-1 border rounded-lg text-sm disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  disabled={page >= totalPages}
                  onClick={() =>
                    setPage((prev) => Math.min(totalPages, prev + 1))
                  }
                  className="px-3 py-1 border rounded-lg text-sm disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <ConfirmDialog
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, id: "", name: "" })}
        onConfirm={handleDeleteConfirm}
        title="Delete Counter"
        message={`Are you sure you want to delete "${deleteModal.name}"? This action cannot be undone.`}
        confirmText="Delete"
        isLoading={isDeleting}
        variant="danger"
      />
    </div>
  );
}
